import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ReportTypeLabels,
  reportTypes,
  reportCadences,
  reportDows,
  type ReportType,
  type ReportCadence,
  type ReportDow,
  type ScheduledReportDto,
} from '@census/shared';
import { api } from '../../shared/api';
import { useAuth } from '../../shared/auth';
import { Modal } from '../../ui/Modal';
import { Icon } from '../../ui/Icon';

interface FormState {
  name: string;
  reportType: ReportType;
  cadence: ReportCadence;
  hourLocal: number;
  minuteLocal: number;
  dayOfWeek: ReportDow | null;
  recipients: string;
  enabled: boolean;
  attachCsv: boolean;
}

const EMPTY_FORM: FormState = {
  name: 'Daily Attendance Sheet',
  reportType: 'DAILY_ATTENDANCE_SHEET',
  cadence: 'WEEKDAYS',
  hourLocal: 7,
  minuteLocal: 0,
  dayOfWeek: null,
  recipients: '',
  enabled: true,
  attachCsv: true,
};

/**
 * Management page for recurring report emails. Admin-only — the API enforces
 * @Roles('ADMIN') on every mutation, and we hide the UI for non-admins too.
 * Non-admins land here via /scheduled-reports get bounced back to / .
 */
export function ScheduledReportsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<ScheduledReportDto | null>(null);
  const [creating, setCreating] = useState(false);
  const [previewType, setPreviewType] = useState<ReportType | null>(null);

  const { data: list, isLoading } = useQuery({
    queryKey: ['scheduled-reports'],
    queryFn: () => api.get<ScheduledReportDto[]>('/scheduled-reports').then((r) => r.data),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/scheduled-reports/${id}`).then((r) => r.data),
    onSuccess: () => {
      toast.success('Schedule deleted');
      qc.invalidateQueries({ queryKey: ['scheduled-reports'] });
    },
  });

  const runNow = useMutation({
    mutationFn: (id: string) => api.post(`/scheduled-reports/${id}/run`).then((r) => r.data),
    onSuccess: (r: any) => {
      if (r?.status === 'SENT') toast.success(`Sent — ${r.rowCount} rows`);
      else if (r?.status === 'DRY_RUN') toast.message(`Dry-run — ${r.error ?? 'SMTP disabled'} (${r.rowCount} rows would have been sent)`);
      else toast.error(r?.error ?? 'Send failed');
      qc.invalidateQueries({ queryKey: ['scheduled-reports'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Run failed'),
  });

  if (user?.role !== 'ADMIN') {
    return (
      <div className="card p-6 text-sm text-ink-subtle">
        Scheduled reports management is admin-only.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold">Scheduled reports</h1>
          <p className="text-sm text-ink-subtle">
            Email reports to management on a schedule. Configure SMTP under
            Settings → SMTP. Runs are evaluated every 5 minutes in the clinic's local timezone.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setCreating(true)}>
          + New scheduled report
        </button>
      </header>

      {isLoading ? (
        <div className="card p-4 text-sm text-ink-subtle">Loading…</div>
      ) : !list || list.length === 0 ? (
        <div className="card p-6 text-center text-sm text-ink-subtle">
          No scheduled reports yet. Click "+ New scheduled report" to send the
          first one (e.g. the daily attendance sheet at 7 AM to management).
        </div>
      ) : (
        <section className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-surface-subtle text-2xs uppercase tracking-wide text-ink-subtle">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Report</th>
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-left">Recipients</th>
                <th className="px-3 py-2 text-left">Last run</th>
                <th className="px-3 py-2 text-left">Next run</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.id} className="border-t border-line-subtle hover:bg-surface-subtle/40">
                  <td className="px-3 py-2 font-medium">
                    {s.name}
                    {!s.enabled && <span className="ml-2 pill-muted">disabled</span>}
                    {s.attachCsv && <span className="ml-2 text-2xs text-ink-subtle">CSV</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">{ReportTypeLabels[s.reportType]}</td>
                  <td className="px-3 py-2 text-xs">{describeSchedule(s)}</td>
                  <td className="px-3 py-2 text-xs">
                    <div className="max-w-[18rem] truncate" title={s.recipients.join(', ')}>
                      {s.recipients.length} · {s.recipients.join(', ')}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {s.lastRunAt ? (
                      <span title={s.lastError ?? ''}>
                        {new Date(s.lastRunAt).toLocaleString()}{' '}
                        <span className={lastStatusCls(s.lastStatus)}>{s.lastStatus}</span>
                      </span>
                    ) : (
                      <span className="text-ink-subtle">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {s.nextRunAt ? new Date(s.nextRunAt).toLocaleString() : <span className="text-ink-subtle">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        className="btn-ghost h-7 px-2 text-xs"
                        onClick={() => runNow.mutate(s.id)}
                        disabled={runNow.isPending}
                        title="Send now (does not change cadence)"
                      >
                        <Icon name="bell" size={12} /> Run
                      </button>
                      <button className="btn-ghost h-7 px-2 text-xs" onClick={() => setPreviewType(s.reportType)}>
                        Preview
                      </button>
                      <button className="btn-ghost h-7 px-2 text-xs" onClick={() => setEditing(s)}>
                        Edit
                      </button>
                      <button
                        className="btn-ghost h-7 px-2 text-xs text-rose-700"
                        onClick={() => {
                          if (confirm(`Delete schedule "${s.name}"?`)) remove.mutate(s.id);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {creating && (
        <EditModal
          initial={EMPTY_FORM}
          onClose={() => setCreating(false)}
          onSave={async (form) => {
            await api.post('/scheduled-reports', form);
            qc.invalidateQueries({ queryKey: ['scheduled-reports'] });
            setCreating(false);
            toast.success('Schedule created');
          }}
        />
      )}
      {editing && (
        <EditModal
          initial={{
            name: editing.name,
            reportType: editing.reportType,
            cadence: editing.cadence,
            hourLocal: editing.hourLocal,
            minuteLocal: editing.minuteLocal,
            dayOfWeek: editing.dayOfWeek,
            recipients: editing.recipients.join(', '),
            enabled: editing.enabled,
            attachCsv: editing.attachCsv,
          }}
          onClose={() => setEditing(null)}
          onSave={async (form) => {
            await api.patch(`/scheduled-reports/${editing.id}`, form);
            qc.invalidateQueries({ queryKey: ['scheduled-reports'] });
            setEditing(null);
            toast.success('Schedule updated');
          }}
        />
      )}
      {previewType && (
        <PreviewModal reportType={previewType} onClose={() => setPreviewType(null)} />
      )}
    </div>
  );
}

function describeSchedule(s: ScheduledReportDto): string {
  const time = `${String(s.hourLocal).padStart(2, '0')}:${String(s.minuteLocal).padStart(2, '0')}`;
  if (s.cadence === 'DAILY') return `Every day @ ${time}`;
  if (s.cadence === 'WEEKDAYS') return `Weekdays @ ${time}`;
  if (s.cadence === 'WEEKLY' && s.dayOfWeek) return `Every ${s.dayOfWeek} @ ${time}`;
  return `${s.cadence} @ ${time}`;
}

function lastStatusCls(status: string | null): string {
  if (status === 'SENT') return 'pill-success';
  if (status === 'DRY_RUN') return 'pill-info';
  if (status === 'FAILED') return 'pill-danger';
  return 'pill-muted';
}

function EditModal({
  initial,
  onClose,
  onSave,
}: {
  initial: FormState;
  onClose: () => void;
  onSave: (form: FormState) => Promise<void>;
}) {
  const [form, setForm] = useState<FormState>(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Auto-fix dayOfWeek <-> cadence so the API doesn't reject the save with
  // "Weekly requires dayOfWeek" or "dayOfWeek only allowed for WEEKLY".
  function setCadence(c: ReportCadence) {
    if (c === 'WEEKLY') {
      setForm((f) => ({ ...f, cadence: c, dayOfWeek: f.dayOfWeek ?? 'MON' }));
    } else {
      setForm((f) => ({ ...f, cadence: c, dayOfWeek: null }));
    }
  }

  return (
    <Modal open={true} onClose={onClose} title={initial.name ? `Edit: ${initial.name}` : 'New scheduled report'} size="lg">
      <div className="space-y-3">
        <div>
          <label className="label">Name</label>
          <input
            className="input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="label">Report</label>
            <select
              className="input"
              value={form.reportType}
              onChange={(e) => setForm({ ...form, reportType: e.target.value as ReportType })}
            >
              {reportTypes.map((rt) => (
                <option key={rt} value={rt}>
                  {ReportTypeLabels[rt]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Cadence</label>
            <select
              className="input"
              value={form.cadence}
              onChange={(e) => setCadence(e.target.value as ReportCadence)}
            >
              {reportCadences.map((c) => (
                <option key={c} value={c}>
                  {c === 'DAILY' && 'Every day'}
                  {c === 'WEEKDAYS' && 'Weekdays (Mon–Fri)'}
                  {c === 'WEEKLY' && 'Weekly (pick a day)'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Hour (24h, clinic local)</label>
            <input
              type="number"
              className="input"
              min={0}
              max={23}
              value={form.hourLocal}
              onChange={(e) => setForm({ ...form, hourLocal: Number(e.target.value) })}
            />
          </div>
          <div>
            <label className="label">Minute</label>
            <input
              type="number"
              className="input"
              min={0}
              max={59}
              step={5}
              value={form.minuteLocal}
              onChange={(e) => setForm({ ...form, minuteLocal: Number(e.target.value) })}
            />
          </div>
          {form.cadence === 'WEEKLY' && (
            <div className="sm:col-span-2">
              <label className="label">Day of week</label>
              <select
                className="input"
                value={form.dayOfWeek ?? 'MON'}
                onChange={(e) => setForm({ ...form, dayOfWeek: e.target.value as ReportDow })}
              >
                {reportDows.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div>
          <label className="label">Recipients</label>
          <textarea
            className="input min-h-[80px]"
            placeholder="alice@yourclinic.com, bob@yourclinic.com"
            value={form.recipients}
            onChange={(e) => setForm({ ...form, recipients: e.target.value })}
          />
          <p className="mt-1 text-2xs text-ink-subtle">Comma or newline separated. Validated before save.</p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex items-center gap-2 rounded border border-slate-200 px-3 py-2 text-sm">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            />
            {form.enabled ? 'Enabled' : 'Disabled'}
          </label>
          <label className="flex items-center gap-2 rounded border border-slate-200 px-3 py-2 text-sm">
            <input
              type="checkbox"
              checked={form.attachCsv}
              onChange={(e) => setForm({ ...form, attachCsv: e.target.checked })}
            />
            Attach CSV
          </label>
        </div>

        {err && <div className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</div>}
      </div>
      <footer className="mt-4 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn-primary"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setErr(null);
            try {
              await onSave(form);
            } catch (e: any) {
              setErr(e?.response?.data?.message ?? 'Save failed');
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </footer>
    </Modal>
  );
}

/**
 * Renders the report HTML server-side and shows it in an iframe srcdoc. Lets
 * admins eyeball what'll arrive in management's inbox before scheduling.
 */
function PreviewModal({ reportType, onClose }: { reportType: ReportType; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['scheduled-report-preview', reportType],
    queryFn: () =>
      api.post('/scheduled-reports/preview', { reportType }).then((r) => r.data),
  });
  return (
    <Modal open={true} onClose={onClose} title={`Preview: ${ReportTypeLabels[reportType]}`} size="xl">
      {isLoading || !data ? (
        <p className="text-sm text-ink-subtle">Rendering…</p>
      ) : (
        <div className="space-y-3">
          <div className="text-xs text-ink-subtle">
            <strong>Subject:</strong> {data.subject}
            <span className="ml-3">
              window {data.window.from} → {data.window.to} · {data.rowCount} rows · {data.csvBytes} CSV bytes
            </span>
          </div>
          <iframe
            srcDoc={data.html}
            className="h-[60vh] w-full rounded border border-line"
            title="Report preview"
          />
        </div>
      )}
    </Modal>
  );
}
